"use client";
import React from "react";
import Divider from "antd/es/divider";
import Drawer from "antd/es/drawer";
import Modal from "antd/es/modal";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import styles from "./AppointmentModal/appointmentFlow.module.css";

type Props = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  showDivider?: boolean;
  /** Extra root class, e.g. `openings-booking-portal` under a booking skin. */
  className?: string;
};

const ResponsiveDialog: React.FC<Props> = ({
  open,
  title,
  onClose,
  children,
  showDivider = true,
  className,
}) => {
  const { matches, ready } = useMediaQuery("(min-width: 768px)");

  // Latch the layout while the dialog is open. Swapping Modal ↔ Drawer when
  // the width crosses 768px (rotation, split view, a resized window) remounts
  // the children, and the booking flow keeps its step, entered contact, code
  // and create idempotency key in local state (BF-DRIFT-021).
  const [openedAsDesktop, setOpenedAsDesktop] = React.useState<
    boolean | null
  >(null);
  if (open && ready && openedAsDesktop === null) {
    setOpenedAsDesktop(matches);
  } else if (!open && openedAsDesktop !== null) {
    setOpenedAsDesktop(null);
  }
  const isDesktop = openedAsDesktop ?? matches;

  if (!ready) return null;

  const rootClassName = [
    styles.appointmentDialog,
    "openings-appointment-dialog",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return isDesktop ? (
    <Modal
      rootClassName={rootClassName}
      title={title}
      open={open}
      maskClosable={false}
      onCancel={onClose}
      footer={null}
      style={{ top: 20 }}
      destroyOnHidden
    >
      {showDivider && <Divider dashed />}
      {children}
    </Modal>
  ) : (
    <Drawer
      rootClassName={rootClassName}
      title={title}
      placement="bottom"
      open={open}
      onClose={onClose}
      destroyOnHidden
      autoFocus={false}
      zIndex={10000}
      styles={{
        wrapper: { height: "100%" },
        body: { position: "relative" },
        mask: { background: "transparent" },
      }}
    >
      {children}
    </Drawer>
  );
};

export default ResponsiveDialog;
